import { useMutation } from '@apollo/client/react'
import React, { FunctionComponent, useContext } from 'react'
import styled from 'styled-components'
import * as serviceWorker from '../../serviceWorker'
import { Page } from '../../components/layout'
import { setFatalErrorNotification } from '../../components/responses/setErrorNotifications'
import Background from './background.png'
import { LoginForm } from './LoginForm'
import { useTheme } from '../ThemeProvider'
import { DispatchStore, isMfaPending, StateStore, userFromToken } from '../StoreProvider'
import { LOGIN_MUTATION } from './loginMutation'
import {
  isRunningInWebview,
  storeLogin,
} from '../../graphql-client/storedState'
import { setCurrentUser } from '../StoreProvider/actions'
import { useAutoLogin } from './useAutoLogin'
import { Loading } from '../../components/responses'
import { canSendMessageToWebView, sendMessageToWebView } from '../../util/react-native'
import { WebMessageType } from '../../util/react-native/types'
import { LOGIN_CLIENTSTATE_MUTATION } from '../../graphql-client/graphql-client'
import { formatMessageSafe } from '../../util/translateInputOptions'
import { defineMessages, useIntl } from 'react-intl'

const messages = defineMessages({
  login: {
    id: 'containers.login.login',
    defaultMessage: 'login',
  },
  username: {
    id: 'containers.login.username',
    defaultMessage: 'username',
  },
  password: {
    id: 'containers.login.password',
    defaultMessage: 'password',
  },
})
const PageWithBackground = styled(Page as any)<{ darkColors?: boolean }>`
  background: ${props =>
    !props.darkColors
      ? `linear-gradient(rgba(250, 191, 64, 0.5) 0%, rgba(250, 191, 64, 1) 35%, rgba(250, 191, 64, 1) 100%), url(${Background});`
      : `linear-gradient(rgb(55 37 0 / 50%) 0%, rgb(43 42 39) 35%, rgba(250,191,64,0.1) 100%), url(${Background});`} 
  background-position: top center;
  background-size: 100% auto;
  background-repeat: no-repeat;
  border-top: 0;
`
const LoginPage: FunctionComponent = () => {
  const intl = useIntl();
  const { state } = useContext(StateStore)
  const formatMessage = formatMessageSafe(intl)
  const { currentTheme } = useTheme()
  const { dispatch } = useContext(DispatchStore)

  const [loginClientState] = useMutation(LOGIN_CLIENTSTATE_MUTATION, {
    onError(err) {
      setFatalErrorNotification(err)
    },
  })

  const [login, { loading: loginLoading, error, data }] = useMutation(LOGIN_MUTATION, {
    onCompleted(data, clientOptions) {
      if (data.login.__typename === 'LoginSuccess') {
        loginClientState()
        const { accessToken, refreshToken, locationToken } = data.login
        storeLogin({ accessToken, refreshToken, locationToken })
        const user = userFromToken(accessToken)
        dispatch(setCurrentUser(user))

        if (!isRunningInWebview() && !isMfaPending(user)) {
          serviceWorker.registerNotificationWorker()
        }

        if (isRunningInWebview() && canSendMessageToWebView()) {
            sendMessageToWebView({ type: WebMessageType.CREDENTIALS, data: JSON.stringify({ user: { ...clientOptions?.variables } }) })
        }
      }
    },
  })

  const { isAutoLoginInProgress } = useAutoLogin({
    currentUser: state.currentUser,
    onSuccess: (token) => {
      const user = userFromToken(token)
      dispatch(setCurrentUser(user))
      if (!isRunningInWebview() && !isMfaPending(user)) {
         serviceWorker.registerNotificationWorker()
      }
    }
  })

  if (isAutoLoginInProgress) {
    return <Loading />
  }

  return (
    <PageWithBackground darkColors={currentTheme === 'dark'} color="yellow" justify="center">
      <LoginForm
        loading={loginLoading}
        login={(credentials) => {
          login({ variables: {...credentials } })
        }}
        loginFailed={!!error || data?.login.__typename === "InvalidCredentialsError"}
        loginLabel={formatMessage(messages.login)}
        passwordPlaceholder={formatMessage(messages.password)}
        usernamePlaceholder={formatMessage(messages.username)}
      />
    </PageWithBackground>
  )
}

export default LoginPage